import { faXmark } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { type ReactNode, useState } from "react";

import { round } from "@repo/system";
import { T, useT } from "@repo/transifex";
import { cn } from "~/lib/utils";

import {
  makeNutritionConverter,
  type NutritionServingBasis,
  type NutritionalInformation
} from "./nutrition-climate-utils";
import { ServingBasisToggle } from "./serving-basis-toggle";

/** Nutrient fields of the nutrition data, in EU label order. */
type NutrientKey =
  | "energy"
  | "fat"
  | "fatSaturated"
  | "fatMonounsaturated"
  | "fatPolyunsaturated"
  | "carbohydrates"
  | "starch"
  | "sugar"
  | "protein"
  | "fibre"
  | "sodium"
  | "salt";

type NutritionRow = {
  /** Field read off the nutrition data. */
  key: NutrientKey;
  /** Translatable nutrient name, e.g. "Protein". */
  label: ReactNode;
  /** Unit suffix appended to the value, e.g. "g" or "kcal". */
  unit: string;
  /** Main macro (energy, fat, carbohydrate, protein): semibold instead of regular. */
  emphasis?: boolean;
  /** Indented breakdown row (saturated fat, sugars): padded, with a muted label. */
  sub?: boolean;
};

/**
 * The full nutrition table, in the order required by the design (EU label):
 * the main macros are semibold (`emphasis`); their breakdown rows (saturated
 * fat, sugars) are indented with a muted label (`sub`); the rest are regular.
 */
const NUTRITION_ROWS: NutritionRow[] = [
  { key: "energy", label: <T _str="Energy" />, unit: "kcal", emphasis: true },
  { key: "fat", label: <T _str="Fat" />, unit: "g", emphasis: true },
  {
    key: "fatSaturated",
    label: <T _str="Saturated fat" />,
    unit: "g",
    sub: true
  },
  {
    key: "fatMonounsaturated",
    label: <T _str="Monounsaturated fat" />,
    unit: "g",
    sub: true
  },
  {
    key: "fatPolyunsaturated",
    label: <T _str="Polyunsaturated fat" />,
    unit: "g",
    sub: true
  },
  {
    key: "carbohydrates",
    label: <T _str="Carbohydrate" />,
    unit: "g",
    emphasis: true
  },
  { key: "starch", label: <T _str="Starch" />, unit: "g", sub: true },
  { key: "sugar", label: <T _str="Sugars" />, unit: "g", sub: true },
  { key: "protein", label: <T _str="Protein" />, unit: "g", emphasis: true },
  { key: "fibre", label: <T _str="Fibre" />, unit: "g" },
  { key: "sodium", label: <T _str="Sodium" />, unit: "mg" },
  { key: "salt", label: <T _str="Salt" />, unit: "g" }
];

type NutritionDetailPanelProps = {
  /** Full nutrition data. Rows whose value is missing (null/undefined) are omitted. */
  nutrition: NutritionalInformation;
  /** Closes the panel. The close button is hidden when omitted. */
  onClose?: () => void;
  className?: string;
};

/**
 * Full nutrition table — the inner content opened from `NutritionClimateRow`'s
 * "open full nutrition" chevron. A header with the "Nutrition" title, a
 * self-contained per serving / per 100 g toggle (shown only when the serving
 * size is known) and an optional close button, over the list of nutrient rows.
 * Values are converted to the selected basis and rounded to at most two decimals.
 *
 * Inner content only: the card shell (background, radius, shadow), sizing and
 * any overlay / mounting are the parent's job. Pass layout via `className`.
 */
export const NutritionDetailPanel = ({
  nutrition,
  onClose,
  className
}: NutritionDetailPanelProps) => {
  const [basis, setBasis] = useState<NutritionServingBasis>(
    nutrition.servingBasis ?? "serving"
  );
  const t = useT();

  const canToggle = Boolean(nutrition.servingSize);

  const convert = makeNutritionConverter({
    from: nutrition.servingBasis,
    to: basis,
    servingSize: nutrition.servingSize
  });
  const rows = NUTRITION_ROWS.map((row) => {
    const value = nutrition[row.key];
    return {
      ...row,
      value: typeof value === "number" ? round(convert(value), 2) : null
    };
  }).filter((row) => row.value !== null);

  return (
    <div className={cn("flex flex-col", className)}>
      <div className="flex items-center justify-between gap-4 border-b border-kiosk-border-secondary px-[30px] pb-[18px] pt-6">
        <p className="text-2xl font-bold text-kiosk-text-primary">
          <T _str="Nutrition" />
        </p>

        <div className="flex items-center gap-4">
          {canToggle ? (
            <ServingBasisToggle value={basis} onChange={setBasis} />
          ) : null}

          {onClose ? (
            <button
              type="button"
              onClick={onClose}
              aria-label={t("Close")}
              className="flex size-12 shrink-0 items-center justify-center rounded-full border border-kiosk-border-secondary bg-kiosk-background-secondary text-kiosk-text-primary active:bg-kiosk-border-secondary"
            >
              <FontAwesomeIcon icon={faXmark} aria-hidden className="text-xl" />
            </button>
          ) : null}
        </div>
      </div>

      <div className="flex flex-col px-[30px] pb-[18px] pt-2 text-base text-kiosk-text-primary">
        {rows.map((row) => (
          <div
            key={row.key}
            className={cn(
              "flex items-center justify-between border-b border-kiosk-border-secondary py-[13px] last:border-b-0",
              row.emphasis ? "font-semibold" : "font-normal",
              row.sub && "pl-6"
            )}
          >
            <span className={cn(row.sub && "text-kiosk-text-secondary")}>
              {row.label}
            </span>
            <span>
              {row.value} {row.unit}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};
